
import { defineComponent, onBeforeMount, reactive } from 'vue'

// components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import FailedFetch from '@/common/components/UI/FailedFetch/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import TheTable from '@/common/components/UI/TheTable/index.vue'
import TableScrollWrapper from '@/common/components/UI/TableScrollWrapper/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'

// types
import { ContractsGetToBeAgreedParamsT, ContractToBeAgreedT } from '@/core/types/contracts.types'

// realisations
import { contracts } from '@/core/realisations/contracts.realisation'
import { bids } from '@/core/realisations/bids.realisation'

// constants
import { ContractsToBeAgreedColumns } from '@/core/constants/columns/ContractsToBeAgreed.columns'

// classes
import { DataState } from '@/core/classes/DataState.class'
import { TabT } from '@/core/types/components.types'

// module components
import ToBeAgreedFilters from '@/modules/ContractsModule/components/ToBeAgreedFilters/index.vue'
import UpdateSignersModal from '@/modules/ContractsModule/components/UpdateSignersModal/index.vue'

// composable
import useNotifications from '@/common/composable/useNotifications'

const MAX_NAME_LENGTH_IN_TABLE = 25

export default defineComponent({
    components: {
        'page-title': PageTitle,
        'failed-fetch': FailedFetch,
        'the-button': TheButton,
        'to-be-agreed-filters': ToBeAgreedFilters,
        'update-signers-modal': UpdateSignersModal,
        TheTable,
        TableScrollWrapper,
        TheInput,
        TheSelect,
    },
    setup() {
        const notifications = useNotifications()

        const delete_state = reactive({
            is_loading: false,
        })

        const check_state = reactive({
            is_loading: false,
        })

        const state = reactive({
            contracts: new DataState<ContractToBeAgreedT>({}),
        })

        const edit_state = reactive({
            is_loading: false,
            is_open: false,
            id: null as number | null,
            signers: [] as { email: string; isSigned: boolean; isLawyer: boolean; typeId: number }[],
        })

        const filters_state = reactive({
            limit: 50,
            //
            bin: null as string | null,
            companyShortName: null as string | null,
            email: null as string | null,
            createdAtFrom: null as string | null,
            createdAtTo: null as string | null,
            typeIds: [] as number[],
        })

        const tabs: TabT[] = [
            { id: 1, name: 'Подписанные', path: '/contracts' },
            { id: 2, name: 'На согласовании', path: '/contracts/to-be-agreed' },
        ]

        const loadContracts = async (page: number) => {
            state.contracts.pagination.current_page = page

            const params = {
                page,
                ...filters_state,
            }

            try {
                state.contracts.is_loading = true

                const response = await contracts.getToBeAgreedList(params)

                state.contracts.list = response.data
                state.contracts.pagination.total_pages = response.meta.last_page
                state.contracts.pagination.total_items = response.meta.total

                state.contracts.is_failed = false
                state.contracts.failure.error_state.is_cancel = false
            } catch (error) {
                state.contracts.failure.catchError(error)

                if (!state.contracts.failure.error_state.is_cancel) {
                    state.contracts.is_failed = true
                }
            } finally {
                if (!state.contracts.failure.error_state.is_cancel) {
                    state.contracts.is_loading = false
                }
            }
        }

        const onLimitChange = (limit: number) => {
            filters_state.limit = limit
            loadContracts(state.contracts.pagination.current_page)
        }

        onBeforeMount(() => {
            loadContracts(1)
        })

        const cutName = (name: string): string => {
            if (name && name.length > MAX_NAME_LENGTH_IN_TABLE) {
                let name_parts = name.split('')
                name_parts = name_parts.splice(0, MAX_NAME_LENGTH_IN_TABLE - 1)
                name_parts.push('...')
                return name_parts.join('')
            }

            return name
        }

        const getContractStatus = (is_signed: boolean, type_id: number): string => {
            if (is_signed && type_id === 1) return 'подписал'
            if (is_signed && type_id === 2) return 'согласовал'

            if (!is_signed && type_id === 1) return 'не подписал'
            if (!is_signed && type_id === 2) return 'не согласовал'

            return ''
        }

        const applyFilters = (params: ContractsGetToBeAgreedParamsT) => {
            filters_state.bin = params.bin || null
            filters_state.companyShortName = params.companyShortName || null
            filters_state.email = params.email || null
            filters_state.createdAtFrom = params.createdAtFrom || null
            filters_state.createdAtTo = params.createdAtTo || null
            filters_state.typeIds = params.typeIds || []

            loadContracts(state.contracts.pagination.current_page)
        }

        const resetFilters = () => {
            filters_state.bin = null
            filters_state.companyShortName = null
            filters_state.email = null
            filters_state.createdAtFrom = null
            filters_state.createdAtTo = null
            filters_state.typeIds = []

            loadContracts(state.contracts.pagination.current_page)
        }

        const deleteContract = async (contract_id: number) => {
            const answer = confirm('Вы уверены, что хотите удалить договор?')
            if (!answer) return

            try {
                delete_state.is_loading = true
                await contracts.deleteContract(contract_id)

                notifications.notify({
                    title: 'Договор удален',
                    type: 'success',
                })
            } catch (error: any) {
                if (error.length) {
                    notifications.notifyError({
                        error,
                        title: 'При удалении договора произошла ошибка',
                    })
                } else {
                    notifications.notify({
                        title: 'При удалении договора произошла ошибка',
                        type: 'danger',
                    })
                }
            } finally {
                delete_state.is_loading = false

                loadContracts(1)
            }
        }

        const checkIsSigned = async (contract_id: number) => {
            try {
                check_state.is_loading = true
                await contracts.checkIsSigned(contract_id)

                loadContracts(1)
                notifications.notify({
                    title: 'Договор успешно проверен!',
                    type: 'success',
                })
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: 'При проверке договора произошла ошибка',
                })
            } finally {
                check_state.is_loading = false
            }
        }

        const toggleEditSigners = (item?: ContractToBeAgreedT) => {
            if (item) {
                edit_state.id = item.id
                edit_state.signers = item.signers.map((signer) => {
                    return {
                        email: signer.email,
                        typeId: signer.typeId,
                        isLawyer: signer.isLawyer,
                        isSigned: signer.isSigned,
                    }
                })
            }

            edit_state.is_open = !edit_state.is_open
        }

        const editSigners = async (signersData: { email: string; newEmail: string | null; dogovor24TypeId: number;}[]) => {
            try {
                edit_state.is_loading = true

                await bids.editSigners(edit_state.id as number, { signersData })

                notifications.notify({
                    title: 'Редактирование успешно завершено!',
                    type: 'success',
                })

                toggleEditSigners()
                loadContracts(state.contracts.pagination.current_page)
            } catch (error) {
                console.error(error)

                notifications.notifyError({
                    error,
                    title: 'При редактировании произошла ошибка',
                })
            } finally {
                edit_state.is_loading = false
            }
        }

        return {
            state,
            filters_state,
            delete_state,
            check_state,
            edit_state,
            //
            loadContracts,
            cutName,
            getContractStatus,
            applyFilters,
            resetFilters,
            deleteContract,
            onLimitChange,
            checkIsSigned,
            toggleEditSigners,
            editSigners,
            //
            ContractsToBeAgreedColumns,
            tabs,
        }
    },
})
